import { GMAIL_SIGNATURE_MAX_CHARS, type RenderMode, renderSignature } from '../../../supabase/functions/_shared/core/index.ts';
import { field, h, mount, toast, toLocalInput } from '../dom.ts';
import { publicBaseUrl } from '../config.ts';
import type { Store } from '../state.ts';
import { signatureFrame } from './sigframe.ts';

export function previewView(store: Store): HTMLElement {
  const root = h('div', { class: 'split' });
  const out = h('div', { class: 'panel' });
  // Seules les boîtes gérées : rien ne sera jamais poussé aux autres, le sélecteur ne doit pas laisser croire l'inverse.
  const unmanaged = store.people.filter((p) => !p.managed);
  const personSel = h(
    'select',
    null,
    store.people.filter((p) => p.managed).map((p) =>
      h(
        'option',
        { value: p.id },
        `${
          p.display_name ||
          [p.first_name, p.last_name].filter(Boolean).join(' ') ||
          p.primary_email
        } — ${p.primary_email}`,
      )
    ),
  );
  const aliasInput = h('input', {
    type: 'email',
    placeholder: 'vide = adresse principale',
  });
  const modeSel = h(
    'select',
    null,
    h(
      'option',
      { value: 'gmail' },
      'Gmail (web + mobile) — poussée automatique',
    ),
    h(
      'option',
      { value: 'local' },
      'Apple Mail / Outlook — installation manuelle',
    ),
  );
  const dateInput = h('input', {
    type: 'datetime-local',
    value: toLocalInput(new Date().toISOString()),
  });

  const render = () => {
    const person = store.people.find((p) => p.id === personSel.value);
    if (!person) {
      return mount(
        out,
        h(
          'p',
          { class: 'muted' },
          'Aucune personne. Synchronisez l’annuaire Google ou ajoutez une boîte.',
        ),
      );
    }
    const sendAs = aliasInput.value.trim().toLowerCase() ||
      person.primary_email;
    const r = renderSignature(
      {
        person,
        sendAsEmail: sendAs,
        brands: store.brands,
        campaigns: store.campaigns,
      },
      {
        mode: modeSel.value as RenderMode,
        publicBaseUrl,
        now: new Date(dateInput.value),
      },
    );
    const pct = Math.min(
      100,
      Math.round((r.length / GMAIL_SIGNATURE_MAX_CHARS) * 100),
    );
    mount(
      out,
      h(
        'div',
        { class: 'meta' },
        h('span', { class: 'chip' }, `Charte : ${r.brand.name}`),
        h(
          'span',
          { class: 'chip' },
          modeSel.value === 'gmail'
            ? (r.campaign ? `Campagne : ${r.campaign.name}` : 'Aucune campagne à cette date')
            : 'Bannière dynamique (résolue à l’affichage)',
        ),
        h(
          'span',
          { class: `chip ${pct > 90 ? 'bad' : ''}` },
          `${r.length.toLocaleString('fr-FR')} / 10 000 caractères`,
        ),
      ),
      h('div', { class: 'gauge' }, h('div', { style: `width:${pct}%` })),
      r.warnings.length
        ? h(
          'ul',
          { class: 'warnings' },
          r.warnings.map((w) => h('li', null, w)),
        )
        : null,
      h('div', { class: 'mail' }, signatureFrame(r.html, { withEmail: true })),
      h(
        'div',
        { class: 'row' },
        h('button', {
          class: 'ghost',
          onclick: async () => {
            await navigator.clipboard.writeText(r.html);
            toast('HTML copié');
          },
        }, 'Copier le HTML'),
      ),
    );
  };
  for (const el of [personSel, aliasInput, modeSel, dateInput]) {
    el.addEventListener('input', render);
  }

  mount(
    root,
    h(
      'div',
      { class: 'panel side' },
      h('h2', null, 'Aperçu'),
      field('Personne / boîte', personSel, unmanaged.length ? `${unmanaged.length} boîte${unmanaged.length > 1 ? 's' : ''} non gérée${unmanaged.length > 1 ? 's' : ''} masquée${unmanaged.length > 1 ? 's' : ''} (${unmanaged.map((p) => p.primary_email.split('@')[0] + '@').join(', ')}) : rien ne leur sera jamais poussé.` : undefined),
      field(
        'Adresse d’envoi (alias)',
        aliasInput,
        'Pour tester un alias d’un autre domaine, ex. prenom@virage17.paris',
      ),
      field(
        'Où la signature est utilisée',
        modeSel,
        'Les deux coexistent pour une même personne : Gmail est poussé automatiquement, le logiciel mail s’installe en plus.',
      ),
      field(
        'Simuler la date',
        dateInput,
        'Pour vérifier qu’une campagne s’affiche au bon moment',
      ),
    ),
    out,
  );
  render();
  return root;
}
